import Button from "@shared/components/ui/Button";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import theme from "@shared/utils/themeColors";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import StudyPlanContent from "../components/StudyPlanContent";
import "../components/StudyPlanContent.css";
import { StudyPlanService } from "../services/study-plan.service";
import {
  StudyPlanGenerationStatus,
  StudyPlanStreamEvent,
  StudyPlan as StudyPlanType,
} from "../types/study-plan.types";

/**
 * Study Plan page component
 */
const StudyPlan: React.FC = () => {
  const navigate = useNavigate();
  const { testProgressId } = useParams<{ testProgressId?: string }>();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationContent, setGenerationContent] = useState("");
  const [closeStream, setCloseStream] = useState<(() => void) | null>(null);

  // Fetch the study plan
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["study-plan", testProgressId],
    queryFn: () => StudyPlanService.getStudyPlan(testProgressId),
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnReconnect: true,
    gcTime: 0,
  });

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  // Handle stream events
  const handleStreamEvent = useCallback(
    (event: StudyPlanStreamEvent) => {
      switch (event.type) {
        case "start":
          console.log("Study plan generation started");
          break;
        case "content":
          if (event.content) {
            setGenerationContent((prev) => prev + event.content);
          }
          break;
        case "complete":
          console.log("Study plan generation completed");
          setIsGenerating(false);
          refetch();
          break;
        case "error":
          console.error("Study plan generation error:", event.error);
          setIsGenerating(false);
          break;
      }
    },
    [refetch]
  );

  // Start the study plan generation
  const startGeneration = useCallback(async () => {
    // Use either the testProgressId from the URL parameter or from the data
    const progressId = testProgressId || data?.testProgressId;
    if (!progressId) return;

    setIsGenerating(true);
    setGenerationContent("");

    // Close any existing stream
    if (closeStream) {
      closeStream();
      setCloseStream(null);
    }

    // Start a new stream
    const close = StudyPlanService.streamStudyPlanGeneration(
      progressId,
      handleStreamEvent
    );

    setCloseStream(() => close);
  }, [testProgressId, data?.testProgressId, handleStreamEvent, closeStream]);

  // Clean up the stream on unmount
  useEffect(() => {
    return () => {
      if (closeStream) {
        closeStream();
      }
    };
  }, [closeStream]);

  // If we have a testProgressId from URL and no study plan yet, start generation automatically
  useEffect(() => {
    if (
      testProgressId &&
      !isGenerating &&
      !data?.studyPlan &&
      !closeStream &&
      !isLoading
    ) {
      startGeneration();
    }
  }, [
    testProgressId,
    isGenerating,
    data?.studyPlan,
    closeStream,
    isLoading,
    startGeneration,
  ]);

  // Determine if we need to show the generation UI
  const showGeneration =
    isGenerating ||
    data?.generationProgress?.status === StudyPlanGenerationStatus.IN_PROGRESS;

  // If we have a generation in progress, but we're not streaming, start streaming
  useEffect(() => {
    if (
      data?.generationProgress?.status ===
        StudyPlanGenerationStatus.IN_PROGRESS &&
      !isGenerating &&
      !closeStream
    ) {
      setIsGenerating(true);
      setGenerationContent(data.generationProgress.content || "");
      startGeneration();
    }
  }, [data, isGenerating, closeStream, startGeneration]);

  // Effect for auto-scroll
  useEffect(() => {
    if (isGenerating && generationContent) {
      const contentElement = document.getElementById("generationContent");
      if (contentElement) {
        contentElement.scrollTop = contentElement.scrollHeight;
      }
    }
  }, [generationContent, isGenerating]);

  // Determine if we need to show the "take diagnostic test" message
  const showTakeDiagnosticTest =
    error && (error as any)?.response?.status === 404 && !testProgressId;

  // Determine if we need to show the "complete diagnostic test" message
  const showCompleteDiagnosticTest =
    error && (error as any)?.response?.status === 400 && !testProgressId;

  // Handle diagnostic test navigation
  const handleTakeDiagnosticTest = async (): Promise<void> => {
    navigate("/diagnostic-test");
    return Promise.resolve();
  };

  const handleCompleteDiagnosticTest = async (): Promise<void> => {
    const testProgressId = (error as any)?.response?.data?.testProgressId;
    if (testProgressId) {
      navigate(`/diagnostic-test/${testProgressId}/take`);
    }
    return Promise.resolve();
  };

  if (error && (error as any)?.errorCode === "NO_DIAGNOSTIC_TEST_PROGRESS") {
    return (
      <AuthenticatedLayout
        isLoading={isLoading || isRefreshing}
        pageHeader={{
          title: "Study Plan",
          refresh: {
            show: true,
            onRefresh: handleRefresh,
          },
        }}
      >
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <div className={`p-4 sm:p-6 lg:p-8`}>
                <div className="flex flex-col items-center justify-center p-4 space-y-4 mb-6 gap-4">
                  {/* Image */}
                  <img
                    className="object-center w-1/2 max-h-96 mb-4"
                    src="/assets/images/no-study-plan.svg"
                    width="760"
                    height="1024"
                    alt="No Study Plan"
                  />
                  <h2 className={`text-5xl font-bold ${theme.text.primary}`}>
                    No study plan yet?
                  </h2>
                  <p className={`text-center ${theme.text.muted}`}>
                    Take your Diagnostic test and we’ll build one just for you.
                  </p>
                  <Button onClick={handleTakeDiagnosticTest}>
                    Take the Test
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  // Prepare empty state content
  let emptyStateContent = {
    title: "No study plan available",
    description: "There is no study plan available at the moment.",
    icon: (
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-gray-200 to-gray-100 mb-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className={`w-8 h-8 ${theme.text.muted}`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
          />
        </svg>
      </div>
    ),
    action:
      testProgressId || data?.testProgressId
        ? {
            label: "Generate Study Plan",
            onClick: startGeneration,
          }
        : undefined,
  };

  // Override empty state for diagnostic test cases
  if (showTakeDiagnosticTest) {
    emptyStateContent = {
      title: "Take a Diagnostic Test First",
      description:
        "You need to take a diagnostic test before we can create a personalized study plan for you.",
      icon: (
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-blue-200 to-blue-100 mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={`w-8 h-8 ${theme.text.primary}`}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-1.17 1.025-3.07 1.025-4.242 0-1.172-1.025-1.172-2.687 0-3.712z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-1.17 1.025-3.07 1.025-4.242 0-1.172-1.025-1.172-2.687 0-3.712z"
            />
          </svg>
        </div>
      ),
      action: {
        label: "Take Diagnostic Test",
        onClick: handleTakeDiagnosticTest,
      },
    };
  } else if (showCompleteDiagnosticTest) {
    emptyStateContent = {
      title: "Complete Your Diagnostic Test",
      description:
        "You need to complete your diagnostic test before we can create a personalized study plan for you.",
      icon: (
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-yellow-200 to-yellow-100 mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={`w-8 h-8 ${theme.text.warning}`}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"
            />
          </svg>
        </div>
      ),
      action: {
        label: "Continue Test",
        onClick: handleCompleteDiagnosticTest,
      },
    };
  }

  return (
    <AuthenticatedLayout
      isLoading={isLoading || isRefreshing}
      error={error}
      isEmpty={!data?.studyPlan && !showGeneration}
      emptyStateProps={emptyStateContent}
      pageHeader={{
        title: "Study Plan",
        refresh: {
          show: true,
          onRefresh: handleRefresh,
        },
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {showGeneration ? (
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <div
                className={`p-4 sm:p-6 lg:p-8 ${theme.bg.primary} rounded-lg shadow`}
              >
                {/* Header with Bee Assistant - Same as in StudyPlanContent */}
                <div className="flex items-center mb-6">
                  <div className="flex-shrink-0 mr-4">
                    <img
                      src="/assets/images/avatar.png"
                      alt="Bee Assistant"
                      className="w-16 h-16 rounded-full"
                    />
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold ${theme.text.primary}`}>
                      Bee Assistant
                    </h2>
                    <p className={`${theme.text.muted}`}>
                      Your Personalized Learning Journey
                    </p>
                  </div>
                </div>

                {/* Loading indicator */}
                <div className="flex flex-col items-center justify-center p-4 space-y-4 mb-6">
                  <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                    Generating your personalized study plan...
                  </h3>
                  <p className={`text-center ${theme.text.muted}`}>
                    Bee Assistant is analyzing your test results and creating a
                    tailored 12-week study plan to help you improve your SAT
                    score.
                  </p>
                  <div className="w-full max-w-md mx-auto h-2 bg-gray-200 dark:bg-gray-700 rounded-full mt-4 overflow-hidden relative">
                    <div className="absolute inset-0 overflow-hidden">
                      <div className="h-full w-1/3 bg-yellow-400 rounded-full animate-progress absolute"></div>
                    </div>
                  </div>
                </div>

                {/* Study plan content - Same format as final result */}
                {generationContent && (
                  <div
                    className="prose prose-sm sm:prose lg:prose-lg max-w-none markdown-content overflow-auto dark:prose-invert"
                    style={{ maxHeight: "800px", scrollBehavior: "smooth" }}
                    id="generationContent"
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Add responsive table support
                        table: ({ node, ...props }) => (
                          <div className="overflow-x-auto">
                            <table {...props} className="min-w-full" />
                          </div>
                        ),
                      }}
                    >
                      {generationContent}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          data?.studyPlan && (
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12">
                <StudyPlanContent studyPlan={data.studyPlan as StudyPlanType} />
              </div>
            </div>
          )
        )}
      </div>
    </AuthenticatedLayout>
  );
};

export default StudyPlan;
